import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Grid3x3, List, Bot } from 'lucide-react';
import { ParentBotCard } from './ParentBotCard';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
  type: string;
}

interface BotMapping {
  id: string;
  parent_bot_id: string;
  child_bot_id: string;
  is_active: boolean;
  notes: string | null;
  instrument: string | null;
  tp_usd: number | null;
  sl_usd: number | null;
  quantity: number | null;
  created_at: string;
  updated_at: string;
  parent_bot: Bot | null;
  child_bot: Bot | null;
}

interface GroupedParent {
  parent: Bot;
  children: BotMapping[];
  totalEndpoints: number;
  activeCount: number;
}

interface ParentBotCardsContainerProps {
  mappings: BotMapping[];
  onToggleActive: (mapping: BotMapping) => void;
  onDelete: (mapping: BotMapping) => void;
  onDeleteComplete: () => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const ParentBotCardsContainer: React.FC<ParentBotCardsContainerProps> = ({
  mappings,
  onToggleActive,
  onDelete,
  onDeleteComplete,
  isUpdating,
  isDeleting,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'partial'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'children' | 'updated'>('name');

  // Group mappings by parent bot
  const groupedByParent = useMemo(() => {
    const grouped = mappings.reduce((acc, mapping) => {
      if (!mapping.parent_bot) return acc;

      const parentId = mapping.parent_bot_id;
      if (!acc[parentId]) {
        acc[parentId] = {
          parent: mapping.parent_bot,
          children: [],
          totalEndpoints: 0,
          activeCount: 0,
        };
      }
      acc[parentId].children.push(mapping);
      acc[parentId].activeCount += mapping.is_active ? 1 : 0;
      return acc;
    }, {} as Record<string, GroupedParent>);

    return Object.values(grouped);
  }, [mappings]);

  // Filter and sort grouped parents
  const filteredParents = useMemo(() => {
    let filtered = groupedByParent;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((group) =>
        group.parent.name.toLowerCase().includes(query) ||
        group.parent.type.toLowerCase().includes(query) ||
        group.children.some(child => 
          child.child_bot?.name.toLowerCase().includes(query)
        )
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((group) => {
        if (statusFilter === 'active') return group.activeCount === group.children.length;
        if (statusFilter === 'inactive') return group.activeCount === 0;
        if (statusFilter === 'partial') return group.activeCount > 0 && group.activeCount < group.children.length;
        return true;
      });
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'name') return a.parent.name.localeCompare(b.parent.name);
      if (sortBy === 'children') return b.children.length - a.children.length;
      if (sortBy === 'updated') {
        const aLatest = Math.max(...a.children.map(c => new Date(c.updated_at).getTime()));
        const bLatest = Math.max(...b.children.map(c => new Date(c.updated_at).getTime()));
        return bLatest - aLatest;
      }
      return 0;
    });

    return filtered;
  }, [groupedByParent, searchQuery, statusFilter, sortBy]);

  const getStatusColor = (group: GroupedParent) => {
    if (group.activeCount === 0) return 'text-muted-foreground';
    if (group.activeCount === group.children.length) return 'text-green-600';
    return 'text-yellow-600';
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 p-4 bg-muted/30 rounded-lg">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Tìm kiếm parent bot hoặc child bot..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Trạng thái" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Sắp xếp" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Theo tên</SelectItem>
              <SelectItem value="children">Số children</SelectItem>
              <SelectItem value="updated">Cập nhật gần</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 bg-card border rounded-lg">
          <div className="text-2xl font-bold">{groupedByParent.length}</div>
          <div className="text-xs text-muted-foreground">Parent Bots</div>
        </div>
        <div className="p-3 bg-card border rounded-lg">
          <div className="text-2xl font-bold">{mappings.length}</div>
          <div className="text-xs text-muted-foreground">Total Children</div>
        </div>
        <div className="p-3 bg-card border rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {mappings.filter(m => m.is_active).length}
          </div>
          <div className="text-xs text-muted-foreground">Active</div>
        </div>
        <div className="p-3 bg-card border rounded-lg">
          <div className="text-2xl font-bold text-muted-foreground">
            {mappings.filter(m => !m.is_active).length}
          </div>
          <div className="text-xs text-muted-foreground">Inactive</div>
        </div>
      </div>

      {/* Parent Bot Cards */}
      {filteredParents.length === 0 ? (
        <div className="text-center py-12 space-y-3">
          <Bot className="h-12 w-12 mx-auto text-muted-foreground/50" />
          <div className="text-muted-foreground">
            {searchQuery
              ? `Không tìm thấy parent bot nào khớp với "${searchQuery}"`
              : 'Chưa có bot mappings. Tạo mapping đầu tiên ở trên.'}
          </div>
        </div>
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4'
              : 'space-y-4'
          }
        >
          {filteredParents.map((group) => (
            <ParentBotCard
              key={group.parent.id}
              parent={group.parent}
              children={group.children}
              activeCount={group.activeCount}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};
